<template>
    <div id="translations-page">
        <loader v-if="metaLoading && !categories.length"></loader>

        <div class="container-fluid" v-else>
            <div class="row">
                <div class="col-12">
                    <div class="box clearfix">
                        <div class="translations-header">
                            <div>
                                <h1>{{ t('settings-translations-title') }}</h1>
                                <p>
                                    {{ t('settings-translations-description') }}
                                </p>
                            </div>
                            <div class="translations-controls">
                                <label for="translations-locale">{{ t('settings-translations-locale') }}</label>
                                <select
                                    id="translations-locale"
                                    v-model="selectedLocale"
                                    class="form-control"
                                    :disabled="metaLoading || rowsLoading"
                                >
                                    <option
                                        v-for="locale in locales"
                                        :key="locale"
                                        :value="locale"
                                    >
                                        {{ locale }}
                                    </option>
                                </select>
                            </div>
                        </div>

                        <div
                            v-if="!categories.length"
                            class="alert alert-info"
                        >
                            {{ t('settings-translations-no_categories_found') }}
                        </div>

                        <div
                            v-else
                            class="translations-layout"
                        >
                            <aside class="translations-sidebar">
                                <button
                                    v-for="category in categories"
                                    :key="category.category"
                                    type="button"
                                    class="category-item"
                                    :class="{
                                        active: category.category === selectedCategory,
                                    }"
                                    @click="selectCategory(category.category)"
                                >
                                    <span class="category-name">
                                        {{ category.category }}
                                    </span>
                                    <span class="category-total">
                                        {{ category.total }}
                                    </span>
                                </button>
                            </aside>

                            <div class="translations-content">
                                <div class="translations-content-header">
                                    <div>
                                        <h2>{{ selectedCategory }}</h2>
                                        <small>
                                            {{ rows.length }} {{ t('settings-translations-items_for') }}
                                            {{ selectedLocale }}
                                        </small>
                                    </div>
                                    <div
                                        v-if="rowsLoading"
                                        class="loading-inline"
                                    >
                                        <span class="fa fa-spin fa-spinner"></span>
                                        {{ t('loading') }}
                                    </div>
                                </div>

                                <div
                                    v-if="!rowsLoading && !rows.length"
                                    class="alert alert-info"
                                >
                                    {{ t('settings-translations-no_translations_found') }}
                                </div>

                                <div
                                    v-else
                                    class="table-responsive"
                                >
                                    <table class="table table-striped table-hover translations-table">
                                        <thead>
                                            <tr>
                                                <th>{{ t('settings-translations-key') }}</th>
                                                <th>{{ t('settings-translations-original_en') }}</th>
                                                <th>{{ t('settings-translations-translated') }} ({{ selectedLocale }})</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr
                                                v-for="row in rows"
                                                :key="row.translation_key"
                                            >
                                                <td class="translation-key-cell">
                                                    <div class="translation-key">
                                                        {{ row.translation_key }}
                                                    </div>
                                                    <small
                                                        v-if="row._status === 'saving'"
                                                        class="status-saving"
                                                    >
                                                        {{ t('settings-translations-saving') }}
                                                    </small>
                                                    <small
                                                        v-else-if="row._status === 'saved'"
                                                        class="status-saved"
                                                    >
                                                        {{ t('settings-translations-saved') }}
                                                    </small>
                                                    <small
                                                        v-else-if="row._status === 'error'"
                                                        class="status-error"
                                                    >
                                                        {{ t('settings-translations-error_saving') }}
                                                    </small>
                                                </td>
                                                <td class="translation-original-cell">
                                                    {{ row.original_content }}
                                                </td>
                                                <td class="translation-input-cell">
                                                    <textarea
                                                        v-model="row._draft"
                                                        class="form-control"
                                                        rows="3"
                                                        :disabled="row._status === 'saving'"
                                                        @blur="saveRow(row)"
                                                    ></textarea>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import keviApi from "../keviApi";

export default {
    data() {
        return {
            metaLoading: false,
            rowsLoading: false,
            categories: [],
            locales: [],
            rows: [],
            selectedCategory: "",
            selectedLocale: "",
            initialized: false,
        };
    },
    computed: {
        place() {
            return this.$store.state.place || {};
        },
        preferredLocale() {
            return this.$route.query.locale || this.place.locale || "";
        },
        preferredCategory() {
            return this.$route.query.category || "";
        },
    },
    created() {
        document.getElementsByTagName("title")[0].innerText =
            "Kevi | " + this.t('settings-translations-title');
        this.loadMeta();
    },
    beforeDestroy() {
    },
    watch: {
        selectedCategory() {
            if (!this.initialized) {
                return;
            }
            this.syncRouteQuery();
            this.loadRows();
        },
        selectedLocale() {
            if (!this.initialized) {
                return;
            }
            this.syncRouteQuery();
            this.loadRows();
        },
    },
    methods: {
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        loadMeta() {
            const options = keviApi.getOptions("translations/meta", "GET");
            this.metaLoading = true;

            this.$http(options).then(
                (response) => {
                    this.categories = response.body.categories || [];
                    this.locales = response.body.locales || [];

                    if (!this.locales.length) {
                        this.locales = ["pt-BR"];
                    }

                    this.selectedLocale = this.pickInitialLocale();
                    this.selectedCategory = this.pickInitialCategory();
                    this.initialized = true;
                    this.loadRows();
                    this.metaLoading = false;
                },
                () => {
                    this.metaLoading = false;
                    this.$store.dispatch(
                        "notification/warning",
                        this.t('settings-translations-unable_to_load'),
                    );
                },
            );
        },
        pickInitialLocale() {
            if (this.locales.includes(this.preferredLocale)) {
                return this.preferredLocale;
            }

            if (this.place.locale && this.locales.includes(this.place.locale)) {
                return this.place.locale;
            }

            return this.locales[0] || "pt-BR";
        },
        pickInitialCategory() {
            const categories = this.categories.map((item) => item.category);

            if (categories.includes(this.preferredCategory)) {
                return this.preferredCategory;
            }

            return categories[0] || "";
        },
        selectCategory(category) {
            if (category === this.selectedCategory) {
                return;
            }
            this.selectedCategory = category;
        },
        loadRows() {
            if (!this.selectedCategory || !this.selectedLocale) {
                this.rows = [];
                return;
            }

            this.rowsLoading = true;

            const options = keviApi.getOptions("translations", "GET", {
                category: this.selectedCategory,
                lang: this.selectedLocale,
            });

            this.$http(options).then(
                (response) => {
                    const items = response.body.items || [];
                    this.rows = items.map((row) => ({
                        ...row,
                        _draft: row.translated_content || "",
                        _lastSaved: row.translated_content || "",
                        _status: "idle",
                    }));
                    this.rowsLoading = false;
                },
                () => {
                    this.rows = [];
                    this.rowsLoading = false;
                    this.$store.dispatch(
                        "notification/warning",
                        this.t('settings-translations-unable_to_load_items'),
                    );
                },
            );
        },
        saveRow(row) {
            const draft = (row._draft || "").trim();
            const lastSaved = (row._lastSaved || "").trim();

            if (draft === lastSaved) {
                return;
            }

            row._status = "saving";

            const options = keviApi.getOptions("translations", "POST", {
                translation_key: row.translation_key,
                lang: this.selectedLocale,
                translated_content: draft,
            });

            this.$http(options).then(
                (response) => {
                    const savedTranslation = response.body.translation;
                    const translatedContent = savedTranslation
                        ? savedTranslation.translated_content || ""
                        : "";

                    row._draft = translatedContent;
                    row._lastSaved = translatedContent;
                    row._status = "saved";

                    window.setTimeout(() => {
                        if (row._status === "saved") {
                            row._status = "idle";
                        }
                    }, 1500);
                },
                () => {
                    row._status = "error";
                },
            );
        },
        syncRouteQuery() {
            const nextQuery = {
                ...this.$route.query,
                category: this.selectedCategory || undefined,
                locale: this.selectedLocale || undefined,
            };

            const currentCategory = this.$route.query.category || "";
            const currentLocale = this.$route.query.locale || "";

            if (
                currentCategory === (nextQuery.category || "") &&
                currentLocale === (nextQuery.locale || "")
            ) {
                return;
            }

            this.$router.replace({
                path: this.$route.path,
                query: nextQuery,
            });
        },
    },
};
</script>

<style lang="less">
#translations-page {
    .box {
        padding: 24px;
    }

    .translations-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-end;
        gap: 16px;
        margin-bottom: 24px;

        h1 {
            margin-bottom: 4px;
        }

        p {
            margin-bottom: 0;
            color: #6c757d;
        }
    }

    .translations-controls {
        min-width: 220px;

        label {
            display: block;
            font-size: 12px;
            font-weight: 600;
            margin-bottom: 6px;
        }
    }

    .translations-layout {
        display: flex;
        gap: 24px;
        align-items: flex-start;
    }

    .translations-sidebar {
        width: 280px;
        max-height: calc(100vh - 220px);
        overflow: auto;
        border: 1px solid #e9ecef;
        border-radius: 8px;
        padding: 8px;
        background: #fafafa;
    }

    .category-item {
        width: 100%;
        border: 0;
        background: transparent;
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 12px;
        text-align: left;
        padding: 10px 12px;
        border-radius: 8px;
        margin-bottom: 6px;
        transition: background-color 0.2s ease;

        &:hover {
            background: #efefef;
        }

        &.active {
            background: #efefef;
            font-weight: 600;
        }
    }

    .category-name {
        word-break: break-word;
    }

    .category-total {
        color: #6c757d;
        font-size: 12px;
        white-space: nowrap;
    }

    .translations-content {
        flex: 1;
        min-width: 0;
    }

    .translations-content-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;

        h2 {
            margin-bottom: 4px;
            font-size: 22px;
        }
    }

    .loading-inline {
        color: #6c757d;
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .translations-table {
        table-layout: fixed;

        th:nth-child(1),
        td:nth-child(1) {
            width: 28%;
        }

        th:nth-child(2),
        td:nth-child(2) {
            width: 32%;
        }

        th:nth-child(3),
        td:nth-child(3) {
            width: 40%;
        }

        td {
            vertical-align: top;
        }

        textarea {
            min-height: 90px;
            resize: vertical;
        }
    }

    .translation-key {
        font-family: monospace;
        font-size: 12px;
        word-break: break-all;
        margin-bottom: 6px;
    }

    .translation-original-cell {
        white-space: pre-wrap;
    }

    .status-saving {
        color: #856404;
    }

    .status-saved {
        color: #1e7e34;
    }

    .status-error {
        color: #bd2130;
    }

    @media (max-width: 991px) {
        .translations-header,
        .translations-content-header,
        .translations-layout {
            flex-direction: column;
            align-items: stretch;
        }

        .translations-sidebar {
            width: 100%;
            max-height: none;
        }

        .translations-controls {
            min-width: 0;
        }
    }
}
</style>
